import { useState } from "react";
import { Link } from "react-router-dom";

export default function Help() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", name: "All Topics", icon: "🔍" },
    { id: "getting-started", name: "Getting Started", icon: "🚀" },
    { id: "exam-creation", name: "Exam Creation", icon: "📝" },
    { id: "student-management", name: "Student Management", icon: "👥" },
    { id: "monitoring", name: "Live Monitoring", icon: "📊" },
    { id: "billing", name: "Billing & Plans", icon: "💳" },
    { id: "security", name: "Security & Privacy", icon: "🔒" },
    { id: "troubleshooting", name: "Troubleshooting", icon: "🔧" }
  ];

  const helpArticles = [
    // Getting Started
    {
      id: 1,
      title: "How to Create Your First Exam",
      category: "getting-started",
      description: "Step-by-step guide to creating your first AI-powered exam using ExamLock's builder",
      readTime: "5 min",
      tags: ["ai-builder", "exam-creation", "beginner"],
      content: "Learn how to use the AI Exam Builder to create professional assessments quickly..."
    },
    {
      id: 2,
      title: "Setting Up Your Teacher Account",
      category: "getting-started", 
      description: "Complete walkthrough of account setup, profile configuration, and initial settings",
      readTime: "3 min",
      tags: ["account", "setup", "profile"],
      content: "Get started with ExamLock by properly configuring your teacher account..."
    },
    {
      id: 3,
      title: "Understanding Student Registration",
      category: "getting-started",
      description: "How students join your class and what information they need to provide",
      readTime: "4 min", 
      tags: ["students", "registration", "class-setup"],
      content: "Learn how to guide students through the registration process..."
    },

    // Exam Creation
    {
      id: 4,
      title: "AI Exam Builder: Complete Guide",
      category: "exam-creation",
      description: "Master the AI-powered exam creation tool for professional assessments",
      readTime: "12 min",
      tags: ["ai-builder", "advanced", "exam-types"],
      content: "Comprehensive guide to using AI to create various types of exams..."
    },
    {
      id: 5,
      title: "Question Types and Best Practices",
      category: "exam-creation",
      description: "Overview of supported question types and when to use each one effectively",
      readTime: "8 min",
      tags: ["questions", "best-practices", "assessment"],
      content: "Learn about multiple choice, essays, and other question formats..."
    },
    {
      id: 6,
      title: "Exam Settings and Time Limits",
      category: "exam-creation",
      description: "Configure exam duration, attempts, and advanced security settings",
      readTime: "6 min",
      tags: ["settings", "time-limits", "security"],
      content: "Optimize your exam settings for the best student experience..."
    },

    // Student Management
    {
      id: 7,
      title: "Adding and Managing Students",
      category: "student-management",
      description: "How to add students to your class and manage their accounts",
      readTime: "7 min",
      tags: ["students", "management", "accounts"],
      content: "Complete guide to student account management and organization..."
    },
    {
      id: 8,
      title: "Assigning Exams to Students",
      category: "student-management",
      description: "Different ways to assign exams and set up exam schedules",
      readTime: "5 min",
      tags: ["assignments", "scheduling", "distribution"],
      content: "Learn how to efficiently distribute exams to your students..."
    },
    {
      id: 9,
      title: "Viewing Student Progress and Results",
      category: "student-management",
      description: "Access detailed analytics and performance data for your students",
      readTime: "9 min",
      tags: ["analytics", "progress", "results"],
      content: "Make data-driven decisions with comprehensive student analytics..."
    },

    // Monitoring
    {
      id: 10,
      title: "Real-time Exam Monitoring",
      category: "monitoring",
      description: "Use live monitoring tools to ensure exam integrity and help struggling students",
      readTime: "10 min",
      tags: ["monitoring", "live", "integrity"],
      content: "Monitor exams in real-time to maintain academic integrity..."
    },
    {
      id: 11,
      title: "Cheating Detection Features",
      category: "monitoring",
      description: "Understanding AI-powered cheating detection and how to respond to alerts",
      readTime: "8 min",
      tags: ["cheating", "detection", "ai"],
      content: "Learn how our AI detects suspicious behavior during exams..."
    },

    // Billing
    {
      id: 12,
      title: "Subscription Plans and Pricing",
      category: "billing",
      description: "Compare plans and choose the right subscription for your needs",
      readTime: "6 min",
      tags: ["pricing", "plans", "subscription"],
      content: "Understand our pricing structure and find the perfect plan..."
    },
    {
      id: 13,
      title: "Managing Your Billing Information",
      category: "billing", 
      description: "Update payment methods, view invoices, and manage your subscription",
      readTime: "4 min",
      tags: ["billing", "payment", "invoices"],
      content: "Keep your billing information current and access payment history..."
    },

    // Security
    {
      id: 14,
      title: "Data Privacy and FERPA Compliance",
      category: "security",
      description: "How ExamLock protects student data and complies with education privacy laws",
      readTime: "7 min",
      tags: ["privacy", "ferpa", "compliance"],
      content: "Understand our commitment to student data privacy..."
    },
    {
      id: 15,
      title: "Account Security Best Practices",
      category: "security",
      description: "Tips for keeping your ExamLock account secure and protecting student data",
      readTime: "5 min",
      tags: ["security", "best-practices", "account"],
      content: "Follow these guidelines to maintain maximum account security..."
    },

    // Troubleshooting
    {
      id: 16,
      title: "Common Login Issues",
      category: "troubleshooting",
      description: "Solve login problems for both teachers and students",
      readTime: "4 min",
      tags: ["login", "troubleshooting", "access"],
      content: "Quick fixes for the most common login and access issues..."
    },
    {
      id: 17,
      title: "Exam Not Loading or Freezing",
      category: "troubleshooting",
      description: "Troubleshoot exam delivery issues and browser compatibility",
      readTime: "6 min",
      tags: ["technical", "browser", "loading"],
      content: "Resolve technical issues that prevent exams from running properly..."
    },
    {
      id: 18,
      title: "Student Can't Access Assigned Exam",
      category: "troubleshooting",
      description: "Debug assignment and access issues for student exams",
      readTime: "5 min",
      tags: ["students", "access", "assignments"],
      content: "Help students who are having trouble accessing their assigned exams..."
    }
  ];

  const quickActions = [
    {
      title: "Start Free Trial",
      description: "Begin your 14-day free trial with full access to all features",
      icon: "🎯",
      link: "/teacher-register",
      color: "bg-blue-500"
    },
    {
      title: "Contact Support", 
      description: "Get personalized help from our education technology experts",
      icon: "💬",
      link: "/contact",
      color: "bg-green-500"
    },
    {
      title: "Watch Demo",
      description: "See ExamLock in action with our guided demo experience",
      icon: "📺",
      link: "/test-demo", 
      color: "bg-purple-500"
    },
    {
      title: "System Status",
      description: "Check real-time status of all ExamLock services",
      icon: "🟢",
      link: "/status",
      color: "bg-emerald-500"
    }
  ];

  const filteredArticles = helpArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              📚 ExamLock Help Center
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Find answers, tutorials, and guides to make the most of ExamLock's 
              AI-powered exam creation and monitoring platform.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for help articles, tutorials, or guides..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 text-gray-900 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-blue-300"
                />
                <div className="absolute right-4 top-4 text-gray-400">
                  <span className="text-2xl">🔍</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">🚀 Quick Actions</h2>
            <p className="text-xl text-gray-600">
              Get started quickly with these common tasks
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.link}
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition duration-300 border border-gray-200"
              >
                <div className={`${action.color} w-16 h-16 rounded-xl flex items-center justify-center text-2xl text-white mb-4 group-hover:scale-110 transition duration-300`}>
                  {action.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{action.title}</h3>
                <p className="text-gray-600 text-sm">{action.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Main Help Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Category Sidebar */}
              <div className="lg:w-1/4">
                <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">📂 Categories</h3>
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition duration-200 flex items-center ${
                          selectedCategory === category.id
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="mr-3">{category.icon}</span>
                        <span className="font-medium">{category.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Articles Grid */}
              <div className="lg:w-3/4">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedCategory === "all" ? "All Articles" : categories.find(c => c.id === selectedCategory)?.name}
                  </h2>
                  <p className="text-gray-600">
                    {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} found
                    {searchQuery && ` for "${searchQuery}"`}
                  </p>
                </div>

                <div className="grid gap-6">
                  {filteredArticles.map((article) => (
                    <div key={article.id} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition duration-300 border border-gray-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2 hover:text-blue-600 cursor-pointer">
                            {article.title}
                          </h3>
                          <p className="text-gray-600 mb-4 leading-relaxed">
                            {article.description}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <span className="mr-1">⏱️</span>
                              {article.readTime} read
                            </span>
                            <span className="flex items-center">
                              <span className="mr-1">{categories.find(c => c.id === article.category)?.icon}</span>
                              {categories.find(c => c.id === article.category)?.name}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-6">
                        {article.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition duration-200">
                          📖 Read Article
                        </button>
                        <div className="text-gray-400">
                          <span className="text-sm">Updated recently</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {filteredArticles.length === 0 && (
                  <div className="text-center py-16">
                    <div className="text-6xl mb-4">🔍</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">No articles found</h3>
                    <p className="text-gray-600 mb-8">
                      Try adjusting your search terms or browse a different category
                    </p>
                    <button
                      onClick={() => {setSearchQuery(""); setSelectedCategory("all");}}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition duration-200"
                    >
                      View All Articles
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Support Section */}
      <div className="py-16 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">💬 Still Need Help?</h2>
            <p className="text-xl text-green-100 mb-8 leading-relaxed">
              Our education technology experts are here to help you succeed. 
              Get personalized support for your specific needs.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">📧</div>
                <div className="font-bold text-lg mb-2">Email Support</div>
                <p className="text-green-100 text-sm">Usually responds within 2 hours during business days</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">💬</div>
                <div className="font-bold text-lg mb-2">Live Chat</div>
                <p className="text-green-100 text-sm">Instant help from our technical support team</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">📞</div>
                <div className="font-bold text-lg mb-2">Phone Support</div>
                <p className="text-green-100 text-sm">Premium subscribers get priority phone support</p>
              </div>
            </div>

            <div className="space-y-4">
              <Link
                to="/contact"
                className="inline-block bg-white text-green-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-green-50 transition duration-200 shadow-lg mx-2"
              >
                💬 Contact Support Team
              </Link>
              <Link
                to="/teacher-register"
                className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-green-600 transition duration-200 mx-2"
              >
                🚀 Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}